import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  X, 
  Calendar,
  Clock,
  Users,
  Plus,
  Trash2
} from 'lucide-react';

interface ScheduleReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ScheduleReviewModal({ isOpen, onClose }: ScheduleReviewModalProps) {
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [reviewTime, setReviewTime] = useState('');
  const [attendees, setAttendees] = useState<string[]>(['']);
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const addAttendee = () => {
    setAttendees([...attendees, '']);
  };

  const removeAttendee = (index: number) => {
    setAttendees(attendees.filter((_, i) => i !== index));
  };

  const updateAttendee = (index: number, value: string) => {
    const newAttendees = [...attendees];
    newAttendees[index] = value;
    setAttendees(newAttendees);
  };

  const handleSchedule = () => {
    // TODO: Implement actual scheduling logic
    alert('Review scheduled successfully!');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">Schedule Review Meeting</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Set up a compliance review meeting for your team
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Review Title */}
          <div>
            <Label htmlFor="title">Meeting Title</Label>
            <Input
              id="title"
              placeholder="e.g., CMS Medicare Advantage Update Review"
              value={reviewTitle}
              onChange={(e) => setReviewTitle(e.target.value)}
              className="mt-2"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Date</Label>
              <div className="relative mt-2">
                <Calendar className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="date"
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="time">Time</Label>
              <div className="relative mt-2">
                <Clock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="time"
                  type="time"
                  value={reviewTime}
                  onChange={(e) => setReviewTime(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <Label htmlFor="duration">Duration</Label>
            <select 
              id="duration"
              className="w-full mt-2 px-3 py-2 border border-border rounded-lg"
            >
              <option>30 minutes</option>
              <option>1 hour</option>
              <option>1.5 hours</option>
              <option>2 hours</option>
            </select>
          </div>

          {/* Attendees */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Attendees</Label>
              <Button variant="outline" size="sm" onClick={addAttendee}>
                <Plus className="w-4 h-4 mr-2" />
                Add Attendee
              </Button>
            </div>
            <div className="space-y-2">
              {attendees.map((attendee, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <Input
                    placeholder="Email address"
                    value={attendee}
                    onChange={(e) => updateAttendee(index, e.target.value)}
                    className="flex-1"
                  />
                  {attendees.length > 1 && (
                    <button
                      onClick={() => removeAttendee(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Meeting Notes (Optional)</Label>
            <textarea
              id="notes"
              rows={4}
              placeholder="Add agenda items, topics to discuss, or any other notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full mt-2 px-3 py-2 border border-border rounded-lg resize-none"
            />
          </div>

          {/* Meeting Link Options */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold mb-2">Meeting Options</h3>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" className="rounded" defaultChecked />
                <span>Send calendar invites to all attendees</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" className="rounded" defaultChecked />
                <span>Include regulatory report in meeting invite</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Record meeting (requires consent)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSchedule}>
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Meeting
          </Button>
        </div>
      </div>
    </div>
  );
}
